import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle, MessageCircle, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUM = "7410121121";
const EMAIL = "Support@futuresanchay.com";
const WHATSAPP_LINK = `https://wa.me/91${PHONE_NUM}?text=Hi,%20I%20want%20to%20discuss%20LIC%20plans`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error("Please fill in required fields");
      return;
    }

    if (formData.phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        message: formData.message || null,
        source: "contact_page"
      });
      setIsSubmitted(true);
      toast.success("Thank you! We'll contact you soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white page-transition" data-testid="contact-page">
      <Helmet>
        <title>Contact FutureSanchay | Free LIC Policy Review | WhatsApp Support</title>
        <meta name="description" content="Contact FutureSanchay for free LIC policy review. WhatsApp us for instant response. Call 7410121121. IRDAI certified advisors. No spam, no pressure." />
        <link rel="canonical" href="https://futuresanchay.com/contact" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-testid="contact-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-emerald-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Contact</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <MessageCircle className="w-4 h-4" />
              We're Here to Help
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 animate-fade-in-up delay-100">
              Get In Touch
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              Have questions about your LIC policy or need guidance? Our IRDAI certified advisors are here to help. <span className="font-semibold text-emerald-500">WhatsApp for fastest response.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-16 md:pb-24">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="order-2 lg:order-1 animate-fade-in-up delay-300">
              {isSubmitted ? (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 text-center animate-scale-in" data-testid="success-message">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Thank You!
                  </h2>
                  <p className="text-gray-600 mb-6">
                    We've received your request. Our advisor will contact you within 24 hours.
                  </p>
                  <p className="text-sm text-gray-500">
                    For immediate assistance, call us at{" "}
                    <a href={`tel:${PHONE_NUM}`} className="text-sky-500 font-medium hover:underline">{PHONE_NUM}</a>
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-6 md:p-8 hover:shadow-2xl transition-all duration-500" data-testid="contact-form">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5 text-sky-500" />
                    </div>
                    Request a Callback
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        className="h-12 bg-white border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 rounded-xl text-base transition-all duration-300 mt-1"
                        required
                        data-testid="input-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        className="h-12 bg-white border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 rounded-xl text-base transition-all duration-300 mt-1"
                        required
                        data-testid="input-phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                        Email (Optional)
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        className="h-12 bg-white border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 rounded-xl text-base transition-all duration-300 mt-1"
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                        Message (Optional)
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us how we can help you..."
                        className="mt-1 min-h-[100px] border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-300"
                        data-testid="input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 bg-sky-500 hover:bg-sky-600 text-white rounded-2xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Sparkles className="w-5 h-5 mr-2" /> Request Callback</>
                      )}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to be contacted by our advisors. <span className="font-medium">Zero spam guaranteed.</span>
                    </p>
                  </form>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="order-1 lg:order-2 animate-fade-in-up delay-400">
              <div className="space-y-6">
                {/* WhatsApp Primary CTA */}
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 opacity-20 animate-gradient"></div>
                  <div className="relative z-10">
                    <h3 className="font-bold text-xl mb-2">Fastest Way to Reach Us</h3>
                    <p className="text-emerald-100 text-sm mb-4">Get instant responses on WhatsApp. Our team is available from 9 AM to 8 PM.</p>
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block">
                      <Button className="w-full h-12 bg-white text-emerald-600 hover:bg-emerald-50 rounded-xl font-bold shadow-md hover:shadow-lg transition-all hover:scale-105" data-testid="whatsapp-btn">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Chat on WhatsApp
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg space-y-4">
                  <h3 className="font-bold text-gray-900 text-lg">Contact Details</h3>
                  
                  <div className="flex items-center gap-4 hover:bg-gray-50 p-2 rounded-xl transition-colors">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <a href={`tel:${PHONE_NUM}`} className="font-semibold text-gray-900 hover:text-sky-500 transition-colors">
                        {PHONE_NUM}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 hover:bg-gray-50 p-2 rounded-xl transition-colors">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <a href={`mailto:${EMAIL}`} className="font-semibold text-gray-900 hover:text-sky-500 transition-colors">
                        {EMAIL}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 hover:bg-gray-50 p-2 rounded-xl transition-colors">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Working Hours</p>
                      <p className="font-semibold text-gray-900">Mon - Sat: 9 AM - 8 PM</p>
                    </div>
                  </div>
                </div>

                {/* Response Time */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Response Time
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      WhatsApp: Within 1 hour (during working hours)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Callback Request: Within 24 hours
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Email: Within 48 hours
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
